import type { AutocompleteOption } from "../../ui/autocomplete";

export type CommodityGroupOptionSource = {
  id: number | string;
  value?: string | number | null;
  parent?: number | string | null;
};

export type CommodityParentOptionSource = {
  id: number | string;
  value?: string | number | null;
};

const compareLabel = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: "base" });

const parentDisplayName = (raw: string) => raw.trim();

const parentGroupKey = (raw: string) => parentDisplayName(raw).toLocaleLowerCase();

/**
 * Combobox options for Curve Management: commodities grouped by parent, A–Z.
 * Selected display uses shortName (longName) → "Commodity (Parent)".
 * Parents that differ only by case or surrounding whitespace share one group header.
 */
export const buildCurveCommodityComboboxOptions = (
  groups: ReadonlyArray<CommodityGroupOptionSource> | null | undefined,
  parents: ReadonlyArray<CommodityParentOptionSource> | null | undefined,
): AutocompleteOption[] => {
  const parentNameById = new Map(
    (parents ?? []).map((parent) => [parent.id.toString(), parentDisplayName((parent.value || parent.id).toString())]),
  );

  const canonicalParentByKey = new Map<string, string>();
  for (const parentName of parentNameById.values()) {
    const key = parentGroupKey(parentName);
    if (key && !canonicalParentByKey.has(key)) {
      canonicalParentByKey.set(key, parentName);
    }
  }

  const items = (groups ?? [])
    .filter((group) => group.parent != null && group.parent !== "")
    .map((group) => {
      const parentId = String(group.parent);
      const parentName = parentNameById.get(parentId) ?? parentDisplayName(parentId);
      const canonicalParent = canonicalParentByKey.get(parentGroupKey(parentName)) ?? parentName;
      const commodityName = (group.value || group.id).toString();
      return {
        value: group.id.toString(),
        label: commodityName,
        groupValue: canonicalParent,
        shortName: commodityName,
        longName: canonicalParent,
      };
    })
    .sort((a, b) => compareLabel(a.groupValue, b.groupValue) || compareLabel(a.label, b.label));

  return items;
};
